import dayjs, { type Dayjs } from "dayjs"

import type {
  PortalCalendarAppointmentItem,
  PortalCalendarEvent,
  PortalCalendarItem,
  PortalCalendarMilestoneItem,
  PortalProjectDetail
} from "../types/portal.types.ts"

/** Real appointments, tagged for the shared item shape every calendar view
 * (Month/Week/Schedule, Home's "Coming up") reads. */
export const asAppointmentItems = (
  events: PortalCalendarEvent[]
): PortalCalendarAppointmentItem[] =>
  events.map((event) => ({ ...event, kind: "appointment" }))

/** Synthesizes read-only "work schedule" entries from a project's own
 * milestones — never a real `CalendarEvent`, so a milestone can never
 * duplicate an appointment the scheduling system already represents. Only
 * meaningful for a single selected project: the customer portal has no bulk
 * "phases across every project" endpoint, so All-projects mode shows
 * appointments only until one exists. */
export const milestonesAsCalendarItems = (
  project: PortalProjectDetail | null
): PortalCalendarMilestoneItem[] => {
  if (!project) return []
  return project.project_phases
    .filter((phase) => phase.start_date || phase.end_date)
    .map((phase) => ({
      kind: "milestone",
      id: phase.id,
      title: phase.name,
      project_id: project.id,
      project_name: project.lead_name,
      start_date: phase.start_date,
      end_date: phase.end_date,
      is_finished: phase.is_finished,
      is_started: phase.is_started
    }))
}

/** The date an item should sort/group under — a milestone's end date when
 * it has one (the date that actually matters to a customer), else its
 * start date; an appointment's real start time. */
export const itemDate = (item: PortalCalendarItem): Dayjs =>
  item.kind === "appointment"
    ? dayjs(item.start_at)
    : dayjs(item.end_date || item.start_date || undefined)

export const itemIsPast = (item: PortalCalendarItem, now: Dayjs): boolean =>
  item.kind === "appointment"
    ? dayjs(item.end_at).isBefore(now)
    : item.is_finished || itemDate(item).isBefore(now, "day")

export const itemIsCancelled = (item: PortalCalendarItem): boolean =>
  item.kind === "appointment" && item.status === "CANCELLED"

/** Home's "Coming up" — the next three relevant items, soonest first,
 * cancelled and past ones excluded (a cancelled appointment must not appear
 * in an upcoming-action list). */
export const upcomingItems = (
  items: PortalCalendarItem[],
  now = dayjs(),
  count = 3
): PortalCalendarItem[] =>
  items
    .filter((item) => !itemIsCancelled(item) && !itemIsPast(item, now))
    .sort((a, b) => itemDate(a).diff(itemDate(b)))
    .slice(0, count)

/** Groups items by calendar day (`YYYY-MM-DD` in the item's own display
 * zone for an appointment, plain for a date-only milestone) — the shape
 * both the Month grid and the Schedule/agenda list read. */
export const groupItemsByDay = (
  items: PortalCalendarItem[]
): Map<string, PortalCalendarItem[]> => {
  const groups = new Map<string, PortalCalendarItem[]>()
  for (const item of items) {
    if (itemIsCancelled(item)) continue
    const key = itemDate(item).format("YYYY-MM-DD")
    const existing = groups.get(key)
    if (existing) existing.push(item)
    else groups.set(key, [item])
  }
  for (const dayItems of groups.values()) {
    dayItems.sort((a, b) => itemDate(a).diff(itemDate(b)))
  }
  return groups
}

export const attendanceLabelKey = (
  item: PortalCalendarAppointmentItem
): "required" | "optional" | "info" => {
  if (item.rsvp_status === null) return "info"
  return item.attendance_required === false ? "optional" : "required"
}

export interface DayTimeBlock {
  item: PortalCalendarAppointmentItem
  /** Minutes from midnight. */
  startMinute: number
  /** Duration in minutes — at least 30, so a very short appointment stays
   * legible/clickable rather than collapsing to a sliver. */
  durationMinutes: number
  /** 0-based column within this day's overlap group. */
  column: number
  /** Total concurrent columns for this day — every block shares the same
   * value so `100 / columns` gives every block an equal, non-overlapping
   * width regardless of which cluster it actually collided with. */
  columns: number
}

/** Positions one day's timed (non-all-day) appointments for the Week
 * grid — never hides an overlapping event behind another, unlike a naive
 * stack: each concurrent event gets its own side-by-side column. */
export const layoutDayTimeBlocks = (
  items: PortalCalendarAppointmentItem[]
): DayTimeBlock[] => {
  const timed = items
    .filter((item) => !item.all_day)
    .map((item) => {
      const dayStart = dayjs(item.start_at).startOf("day")
      const startMinute = dayjs(item.start_at).diff(dayStart, "minute")
      const endMinute = Math.max(
        dayjs(item.end_at).diff(dayStart, "minute"),
        startMinute + 30
      )
      return { item, startMinute, endMinute }
    })
    .sort((a, b) => a.startMinute - b.startMinute)

  const active: { endMinute: number; column: number }[] = []
  const placed: {
    item: PortalCalendarAppointmentItem
    startMinute: number
    endMinute: number
    column: number
  }[] = []
  let maxColumn = 0

  for (const entry of timed) {
    for (let i = active.length - 1; i >= 0; i--) {
      if (active[i].endMinute <= entry.startMinute) active.splice(i, 1)
    }
    const usedColumns = new Set(active.map((a) => a.column))
    let column = 0
    while (usedColumns.has(column)) column++
    active.push({ endMinute: entry.endMinute, column })
    maxColumn = Math.max(maxColumn, column)
    placed.push({ ...entry, column })
  }

  const columns = maxColumn + 1
  return placed.map(({ item, startMinute, endMinute, column }) => ({
    item,
    startMinute,
    durationMinutes: endMinute - startMinute,
    column,
    columns
  }))
}
